import { InfiniteData, QueryClient, QueryKey, UseMutationOptions } from '@tanstack/react-query';
import { toast } from 'sonner';

import { campaignsEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import {
  Campaign,
  CampaignAnalyticsPayload,
  Campaigns,
  PartialCampaign,
} from '@/shared/types/campaigns';
import { EventName } from '@/shared/types/segment';

export interface VerifySubscribersRequest {
  subscribers: Array<{
    phone?: string;
    email?: string;
    context?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
  }>;
  /**
   * When provided, the request is routed to the campaign-scoped verify endpoint
   * (Tier A + Tier B — full DB pipeline ending in ROLLBACK). When omitted, the
   * legacy Tier-A-only endpoint is used (schema validation only). Prefer the
   * scoped variant whenever a campaign exists; if Tier B returns `[]`, the
   * subsequent bulk-upsert of the same body is guaranteed to succeed.
   * See Orchestrator PR #1839 §4.
   */
  campaign_id?: string;
}

/**
 * Per-row error returned by verify and bulk-upsert.
 *
 * As of Orchestrator PR #1839, `field` carries a stable error code that the UI
 * uses to branch on (in addition to the human-readable `message`). Known codes:
 *  - `subscriber.debtExistsWithExtras` (409) — caller sent fields beyond
 *    `debt_external_id` for a debt that already exists. Point the user at
 *    `PATCH /v1/contacts/:id/debts/:debt_id`.
 *  - `subscriber.contactNotFound` (404) — `context.contact_id` missing or from
 *    a different tenant (collapsed to avoid tenant leak).
 *  - Tier-A schema errors like `context.debts.0.parties` — parties/role inside
 *    a debt entry were rejected. Point the user at the Debt API parties route.
 *  - Cross-row shared `debt_external_id` (400, Tier A) — two subscriber rows
 *    share the same external ID. Point the user at the Debt API parties route
 *    for co-borrower setup.
 */
export type VerifySubscribersResponse = Array<{
  index: number;
  field?: string;
  message: string;
  value?: string;
  error?: string; // Keep for backward compatibility
  subscriber?: {
    phone?: string;
    email?: string;
    context?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
  };
}>;

export const API = Object.freeze({
  listAllCampaigns: async (
    pageParam?: { cursor?: string },
    filter?: string,
    limit?: number,
    sort?: string,
    status?: string,
    channels?: string[],
    aiAgents?: string[]
  ): Promise<Campaigns> =>
    callGet(
      endpoints.campaigns(pageParam?.cursor, filter, limit, sort, status, channels, aiAgents)
    ),

  getCampaign: async (campaignId: string): Promise<Campaign> =>
    callGet(endpoints.campaign(campaignId)),

  getCampaignAnalytics: async (campaignId: string): Promise<CampaignAnalyticsPayload> =>
    callGet(endpoints.campaignAnalytics(campaignId)),

  createCampaign: async (newCampaign: Partial<Campaign>, accountSlug: string): Promise<Campaign> =>
    callPost(endpoints.campaigns(), newCampaign, {}, { accountSlug }),

  updateCampaign: async ({ campaign_id, ...campaign }: PartialCampaign): Promise<Campaign> =>
    callPut(endpoints.campaign(campaign_id), campaign),

  deleteCampaign: async (campaignId: string): Promise<Campaign> => {
    return callDelete(`${endpoints.campaign(campaignId)}`);
  },

  verifySubscribers: async (data: VerifySubscribersRequest): Promise<VerifySubscribersResponse> => {
    const { campaign_id, ...payload } = data;
    // Campaign-scoped verify (Tier A + B) when we know the campaign; legacy
    // Tier-A-only verify otherwise. See Orchestrator PR #1839 §4.
    const url = campaign_id
      ? endpoints.campaignSubscribersVerifyScoped(campaign_id)
      : endpoints.campaignSubscribersVerify;
    return callPut(url, payload);
  },
});

/**
 * Matches every cached campaigns list query for the account. The list key layout is
 * [endpoints.campaigns (function), filter, limit, account_id, sort, status, channels, aiAgents]
 * — see campaignsQueryKey in useCampaigns; key[0] is compared by function reference.
 */
const isCampaignsListKey = (key: QueryKey, accountId?: string) =>
  Array.isArray(key) && key[0] === endpoints.campaigns && key[3] === accountId;

export const onCampaignUpdated = (queryClient: QueryClient, campaign: Campaign) => {
  // Update individual campaign cache
  queryClient.setQueryData<Campaign>(
    [endpoints.campaign(campaign.campaign_id)],
    (prev: Campaign) => ({ ...prev, ...campaign })
  );

  // Update function to apply to all matching caches
  // Important: Merge instead of replace to preserve fields like 'senders' that aren't
  // returned by the update API but are included in the list API response
  const updateCampaignInCache = (prev: InfiniteData<Campaigns>) => {
    if (!prev) {
      return prev;
    }

    return {
      ...prev,
      pages: prev.pages.map((page) => ({
        ...page,
        campaigns: page.campaigns.map((item) =>
          item.campaign_id === campaign.campaign_id ? { ...item, ...campaign } : item
        ),
      })),
    };
  };

  // Find and update ALL campaign list queries for this account
  const queryCache = queryClient.getQueryCache();

  const matchingQueries = queryCache.findAll({
    predicate: (query) => isCampaignsListKey(query.queryKey, campaign.account_id),
  });

  matchingQueries.forEach((query) => {
    queryClient.setQueryData(query.queryKey, updateCampaignInCache);
  });
};

export const onCampaignCreated = (queryClient: QueryClient, campaign: Campaign) => {
  const addCampaignToCache = (prev: InfiniteData<Campaigns> | undefined) => {
    if (!prev) {
      return prev;
    }

    const newPages = prev.pages.map((page, index) => {
      if (index === 0) {
        return {
          ...page,
          pagination: { ...page.pagination, total: (page.pagination.total || 0) + 1 },
          campaigns: [
            campaign,
            ...page.campaigns.filter((item) => item.campaign_id !== campaign.campaign_id),
          ],
        };
      }
      return page;
    });

    return { ...prev, pages: newPages };
  };

  // Find and update ALL campaign list queries for this account
  const queryCache = queryClient.getQueryCache();
  queryCache
    .findAll({ predicate: (query) => isCampaignsListKey(query.queryKey, campaign.account_id) })
    .forEach((query) => {
      queryClient.setQueryData(query.queryKey, addCampaignToCache);
    });
};

export const onCampaignRemoved = (
  queryClient: QueryClient,
  account_id: string,
  campaign_id: string
) => {
  const holdsCampaign = (data?: InfiniteData<Campaigns>) =>
    Boolean(
      data?.pages.some((page) => page.campaigns.some((item) => item.campaign_id === campaign_id))
    );

  // Update function to apply to all matching caches
  const removeCampaignFromCache = (prev: InfiniteData<Campaigns>) => {
    if (!prev) {
      return prev;
    }

    return {
      ...prev,
      pages: prev.pages.map((page, index) => {
        if (index === 0) {
          return {
            ...page,
            pagination: {
              ...page.pagination,
              total: Math.max(0, (page.pagination.total || 0) - 1),
            },
            campaigns: page.campaigns.filter((item) => item.campaign_id !== campaign_id),
          };
        }
        return {
          ...page,
          campaigns: page.campaigns.filter((item) => item.campaign_id !== campaign_id),
        };
      }),
    };
  };

  // Find and update ALL campaign list queries for this account
  const queryCache = queryClient.getQueryCache();
  queryCache
    .findAll({ predicate: (query) => isCampaignsListKey(query.queryKey, account_id) })
    .forEach((query) => {
      // Filtering the row out is idempotent; the total decrement is not. Only touch caches that
      // still hold the row, so a replayed removal (socket redelivery, RTM racing the local
      // fan-out) cannot decrement the same total twice. Caches that never held the row — a
      // filtered view, a page never fetched — cannot be corrected blind; the callers own that
      // reconciliation (useRtmSetup schedules a debounced refetch for foreign removals, the
      // bulk mutation invalidates in onSettled for our own).
      if (holdsCampaign(queryClient.getQueryData<InfiniteData<Campaigns>>(query.queryKey))) {
        queryClient.setQueryData(query.queryKey, removeCampaignFromCache);
      }
    });

  // Remove individual campaign query
  queryClient.removeQueries({ queryKey: [endpoints.campaign(campaign_id)] });
};

/**
 * Marks every campaign list cache of the account stale, refetching active ones. Cache surgery
 * (onCampaignRemoved) gives instant feedback, but the refetch is the authority: it repacks pages
 * that shrank (pagination is server-side), corrects totals in caches that never held the removed
 * row, and reconciles rows treated as 404-as-success.
 */
export const invalidateCampaignLists = (queryClient: QueryClient, accountId: string) => {
  queryClient.invalidateQueries({
    predicate: (query) => isCampaignsListKey(query.queryKey, accountId),
  });
};

/** How long to wait for an RTM removal burst to go quiet before the reconciling refetch. */
export const CAMPAIGN_REMOVAL_RECONCILE_MS = 500;

/**
 * Debounces invalidateCampaignLists per account: another user's bulk delete arrives as one
 * campaigns_removed event per campaign, and invalidating on each would cancel and restart the
 * active list refetch once per event. One trailing refetch per burst carries the same
 * information. (`cancelRefetch: false` is not an alternative — an invalidation landing while a
 * refetch is in flight is cleared when that fetch resolves, losing the reconciliation.)
 */
export const createCampaignListReconciler = (queryClient: QueryClient) => {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();

  return {
    schedule: (accountId: string) => {
      clearTimeout(timers.get(accountId));
      timers.set(
        accountId,
        setTimeout(() => {
          timers.delete(accountId);
          invalidateCampaignLists(queryClient, accountId);
        }, CAMPAIGN_REMOVAL_RECONCILE_MS)
      );
    },
    cancel: () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    },
  };
};

export type CampaignListReconciler = ReturnType<typeof createCampaignListReconciler>;

/**
 * The account rides in the variables, not in `mutationFn`. Variables are captured once at
 * `mutate()`; the options object is not — a re-render swaps `mutationFn` on a pending mutation,
 * so a retry after an account switch would write into the new tenant.
 */
export type CreateCampaignVariables = { campaign: Partial<Campaign>; accountSlug: string };

/**
 * The one create-campaign mutation config. Shared so `useCampaigns` (campaigns page) and
 * `useCreateCampaign` (command palette) cannot drift on cache fan-out or error handling.
 */
export const createCampaignMutationOptions = (
  queryClient: QueryClient
): UseMutationOptions<Campaign, Error, CreateCampaignVariables> => ({
  mutationFn: ({ campaign, accountSlug }) => API.createCampaign(campaign, accountSlug),
  meta: {
    segment: {
      success: EventName.CampaignCreated,
      action: 'campaign_create',
      successProps: (campaign: Campaign) => ({ campaign_id: campaign.campaign_id }),
    },
  },
  onSuccess: (resp) => {
    onCampaignCreated(queryClient, resp);
  },
  onError: (error) => {
    toast.error(error.message);
  },
});
